/**
 * Admin Portal — shared UI primitives (Phase 1).
 * All classes scoped under `.admin-portal` (see styles.css).
 * Nothing here mutates global state or shells out to the legacy UI.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, AlertCircle, Check, Inbox, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';

const DUE_SOON_MS = 3 * 24 * 60 * 60 * 1000; // 3 days — same threshold as the legacy dashboard.

/* ────────────────────────────────────────────────────────────
 * Bucketing helper — identical semantics to the legacy
 * `bucketRow` in App.js, so kit-preview counts match.
 * ──────────────────────────────────────────────────────────── */
export const bucketRow = (row) => {
  if (!row.is_returnable || row.return_status === 'consumable') return 'neutral';
  if (row.return_status === 'pending_return') return 'pending';
  if (!row.return_due_date) return 'normal';
  const ts = new Date(row.return_due_date).getTime();
  if (ts < Date.now()) return 'overdue';
  if (ts - Date.now() < DUE_SOON_MS) return 'due_soon';
  return 'normal';
};

/* ─── Card ───────────────────────────────────────────────── */
export const Card = ({ title, subtitle, actions, children, testId, className = '' }) => (
  <section className={`ap-card ${className}`.trim()} data-testid={testId}>
    {(title || actions) && (
      <header className="ap-card-header">
        <div>
          {title && <h3 className="ap-card-title">{title}</h3>}
          {subtitle && <p className="ap-card-subtitle">{subtitle}</p>}
        </div>
        {actions && <div>{actions}</div>}
      </header>
    )}
    {children}
  </section>
);

/* ─── EmptyState ─────────────────────────────────────────── */
export const EmptyState = ({ icon: Icon = Inbox, title, message, action, testId }) => (
  <div className="ap-empty" data-testid={testId}>
    <div className="ap-empty-icon"><Icon size={18} strokeWidth={1.6} /></div>
    <p className="ap-empty-title">{title}</p>
    {message && <p className="ap-empty-lede">{message}</p>}
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

/* ─── StatusPill ─────────────────────────────────────────── */
export const StatusPill = ({ variant = 'neutral', children, testId }) => (
  <span className={`ap-status ap-status-${variant}`} data-testid={testId}>{children}</span>
);

/* Map an order status → StatusPill variant (visual only). */
export const orderStatusVariant = (raw) => {
  const s = (raw || '').toLowerCase();
  if (s === 'pending') return 'warn';
  if (s === 'approved' || s === 'admin_approved') return 'info';
  if (s === 'superadmin_approved') return 'info';
  if (s === 'issued') return 'ok';
  if (s === 'returned' || s === 'completed') return 'ok';
  if (s === 'rejected' || s === 'cancelled' || s === 'clerk_rejected') return 'bad';
  return 'neutral';
};

export const humanStatus = (raw) => {
  if (!raw) return '—';
  return raw.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

/* ─── SearchInput ─────────────────────────────────────────── */
export const SearchInput = ({ value, onChange, placeholder, testId, autoFocus }) => (
  <div className="ap-search">
    <Search size={16} className="ap-search-icon" />
    <input
      type="text"
      className="ap-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      data-testid={testId}
    />
  </div>
);

/* ─── Modal ──────────────────────────────────────────────── */
export const Modal = ({ open, onClose, title, subtitle, wide, children, footer, testId }) => {
  if (!open) return null;
  return (
    <div className="ap-modal-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={`ap-modal${wide ? ' ap-modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <header className="ap-modal-header">
          <div>
            <h3 className="ap-card-title">{title}</h3>
            {subtitle && <p className="ap-card-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="ap-topbar-btn"
            onClick={onClose}
            aria-label="Close"
            data-testid="ap-modal-close"
          ><X size={16} /></button>
        </header>
        <div className="ap-modal-body">{children}</div>
        {footer && <div className="ap-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

/* ─── Toast ──────────────────────────────────────────────── */
export const Toast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const cls = toast.variant === 'ok' ? 'ap-toast ap-toast-ok'
            : toast.variant === 'bad' ? 'ap-toast ap-toast-bad'
            : 'ap-toast';
  const Icon = toast.variant === 'bad' ? AlertCircle : Check;
  return (
    <div className={cls} role="status" data-testid="ap-toast">
      <Icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', opacity: 0.7 }}
      ><X size={16} /></button>
    </div>
  );
};

/* ─── Photo viewer (dark scrim + click-out close) ────────── */
export const PhotoViewer = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div className="ap-photo-scrim" onClick={onClose} data-testid="ap-photo-viewer">
      <img src={src} alt="Preview" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

/* ─── Formatters ─────────────────────────────────────────── */
export const formatShortTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} · ${time}`;
};
export const formatDayOnly = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const shortRef = (id, prefix = 'REF') => {
  if (!id) return `${prefix}-—`;
  const s = String(id).replace(/-/g, '').slice(0, 8).toUpperCase();
  return `${prefix}-${s}`;
};

/* ────────────────────────────────────────────────────────────
 * StudentKitPreview — Read-only view of a student's current
 * outstanding kit, embedded inside each pending request card.
 * Uses GET /api/students/{id}/kit-preview (existing endpoint).
 * ──────────────────────────────────────────────────────────── */
export const StudentKitPreview = ({ studentId, studentName }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    apiClient
      .get(`/students/${studentId}/kit-preview`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not load kit preview.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const items = data?.items || [];
  const counts = useMemo(() => {
    let issued = 0, overdue = 0, due_soon = 0, pending = 0;
    items.forEach((row) => {
      issued += 1;
      const b = bucketRow(row);
      if (b === 'overdue') overdue += 1;
      else if (b === 'due_soon') due_soon += 1;
      else if (b === 'pending') pending += 1;
    });
    return { issued, overdue, due_soon, pending };
  }, [items]);

  const highOverdue = counts.overdue >= 3;

  return (
    <div className="ap-kit-wrap" data-testid={`ap-kit-preview-${studentId}`}>
      {!loading && !error && highOverdue && (
        <div className="ap-kit-alert" role="status" data-testid={`ap-kit-overdue-warning-${studentId}`}>
          <AlertTriangle size={16} className="ap-kit-alert-icon" strokeWidth={1.8} />
          <div>
            <p className="ap-kit-alert-title">
              {counts.overdue} components are overdue for return.
            </p>
            <p className="ap-kit-alert-body">
              {studentName} has {counts.overdue} outstanding overdue item{counts.overdue === 1 ? '' : 's'}. You can still approve this request — this is informational only.
            </p>
          </div>
        </div>
      )}

      <div className="ap-kit-panel">
        <button
          type="button"
          className="ap-kit-toggle"
          onClick={() => setOpen((v) => !v)}
          data-testid={`ap-kit-toggle-${studentId}`}
        >
          <span className="ap-kit-toggle-left">
            <span className="ap-kit-toggle-label">Student's Current Kit</span>
            {!loading && !error && (
              <>
                <span className="ap-kit-count">
                  <strong>{counts.issued}</strong>
                  {counts.issued === 1 ? ' component issued' : ' components issued'}
                </span>
                {counts.due_soon > 0 && (
                  <span className="ap-kit-count is-due-soon">
                    <span className="dot" /><strong>{counts.due_soon}</strong> due soon
                  </span>
                )}
                {counts.overdue > 0 && (
                  <span className="ap-kit-count is-overdue" data-testid={`ap-kit-overdue-count-${studentId}`}>
                    <span className="dot" /><strong>{counts.overdue}</strong> overdue
                  </span>
                )}
                {counts.pending > 0 && (
                  <span className="ap-kit-count is-pending">
                    <span className="dot" /><strong>{counts.pending}</strong> return pending
                  </span>
                )}
              </>
            )}
            <span className="ap-kit-toggle-tag">Read-only</span>
          </span>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {open && (
          <div className={items.length === 0 ? 'ap-kit-body-empty' : 'ap-kit-body'} data-testid={`ap-kit-body-${studentId}`}>
            {loading && <span>Loading kit for {studentName}…</span>}
            {error && <span style={{ color: 'var(--diis-danger)' }}>{error}</span>}
            {!loading && !error && items.length === 0 && (
              <span>{studentName} is currently holding no components.</span>
            )}
            {!loading && !error && items.length > 0 && (
              <>
                <table className="ap-kit-table">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Request</th>
                      <th style={{ textAlign: 'right' }}>Outstanding</th>
                      <th>Due</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const b = bucketRow(row);
                      const cls = b === 'overdue' ? 'is-overdue' : b === 'due_soon' ? 'is-due-soon' : '';
                      return (
                        <tr key={`${row.order_id}-${row.component_id}`} className={cls} data-testid={`ap-kit-row-${row.component_id}`}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{row.component_name || 'Component'}</div>
                            <div className="ap-subtle">{row.brand_name || '—'}{row.size ? ` · ${row.size}` : ''}</div>
                          </td>
                          <td><span className="ap-req-mono">{row.order_code}</span></td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{row.qty_outstanding}</td>
                          <td>{formatDayOnly(row.return_due_date)}</td>
                          <td>
                            {b === 'pending'   && <StatusPill variant="info">Return pending</StatusPill>}
                            {b === 'overdue'   && <StatusPill variant="bad">Overdue</StatusPill>}
                            {b === 'due_soon'  && <StatusPill variant="warn">Due soon</StatusPill>}
                            {b === 'normal'    && <StatusPill variant="ok">In kit</StatusPill>}
                            {b === 'neutral'   && <StatusPill variant="neutral">Consumable</StatusPill>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="ap-kit-caption">Read-only. This view does not permit issuing, returning or otherwise modifying the student's kit.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
